import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TrainingService } from '../training.service';
import { AuthService } from '../../auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TRAINING_SUPER_SIGNER_IDS } from '../../utils/constants';

@Component({
  selector: 'app-employee-training-detail',
  templateUrl: './employee-training-detail.component.html',
  styleUrls: ['./employee-training-detail.component.css']
})
export class EmployeeTrainingDetailComponent implements OnInit {
  enrollment: any = null;
  loading = true;
  processingSignOff = false;
  currentUserId: number;
  user: any;
  /** False when the logged-in user is the employee (trainee viewing own); they cannot sign off. */
  canSignOff = false;
  /** Signoff role IDs the current user is allowed to use (for role-based signoff). Empty = no restriction. */
  userSignoffRoleIds: number[] = [];
  /** True for TRAINING_SUPER_SIGNER_IDS: may sign off anything — any role, locked activities, own enrollment. Backend enforces the same list. */
  isSuperSigner = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private trainingService: TrainingService,
    private auth: AuthService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.auth.userProfile$.subscribe((profile) => {
      this.user = profile;
      if (this.user) {
        this.currentUserId = this.auth.getEmployeeId();
        this.isSuperSigner = this.currentUserId != null
          && TRAINING_SUPER_SIGNER_IDS.indexOf(Number(this.currentUserId)) !== -1;
        if (this.enrollment && this.enrollment.employee_id != null) {
          this.canSignOff = this.currentUserId != null
            && (this.isSuperSigner || Number(this.currentUserId) !== Number(this.enrollment.employee_id));
        }
        this.loadSignoffRoles();
      }
    });

    // Get enrollment ID from route params
    const enrollmentId = this.route.snapshot.params['id'];
    if (enrollmentId) {
      this.loadEnrollment(+enrollmentId);
    }
  }

  /** Load current user's signoff roles (for role-based activity sign-off). */
  loadSignoffRoles(): void {
    if (this.currentUserId == null) {
      this.userSignoffRoleIds = [];
      return;
    }
    this.trainingService.getSignoffRolesForEmployee(this.currentUserId).subscribe(
      (response) => {
        if (!response.Error && response.SignoffRoles && Array.isArray(response.SignoffRoles)) {
          this.userSignoffRoleIds = response.SignoffRoles.map((r: any) => +r.signoff_role_id).filter((id: number) => !isNaN(id));
        } else {
          this.userSignoffRoleIds = [];
        }
        this.cdr.detectChanges();
      },
      () => {
        this.userSignoffRoleIds = [];
        this.cdr.detectChanges();
      }
    );
  }

  /** True if the current user can sign off this activity (role-based: activity's signoff_role_id must be in user's roles, or no role set). */
  canSignOffActivity(activity: any): boolean {
    if (this.isSuperSigner) return true;
    if (!this.canSignOff) return false;
    const roleId = activity.signoff_role_id != null ? +activity.signoff_role_id : null;
    if (roleId == null) return true;
    return this.userSignoffRoleIds.indexOf(roleId) !== -1;
  }

  /** Prereq lock as seen by the current user — super signers can complete locked activities. */
  isActivityLockedForUser(activity: any): boolean {
    return !activity.is_unlocked && !this.isSuperSigner;
  }

  loadEnrollment(id: number): void {
    this.loading = true;
    this.trainingService.getEnrollmentById(id).subscribe(
      (response) => {
        if (!response.Error) {
          this.enrollment = response.Enrollment;
          if (!this.enrollment.training_level) {
            this.enrollment.training_level = 0;
          }
          this.canSignOff = this.currentUserId != null && this.enrollment.employee_id != null
            && (this.isSuperSigner || Number(this.currentUserId) !== Number(this.enrollment.employee_id));
          this.loadSignoffRoles();
        }
        this.loading = false;
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error loading enrollment:', error);
        this.loading = false;
        this.cdr.detectChanges();
      }
    );
  }

  completeActivity(activity: any): void {
    // Prevent multiple simultaneous API calls
    if (this.processingSignOff) {
      this.snackBar.open('Please wait for the current action to complete.', 'Close', {
        duration: 2000
      });
      return;
    }

    if (this.isActivityLockedForUser(activity)) {
      this.snackBar.open('This activity is locked. Please complete prerequisites first.', 'Close', {
        duration: 3000
      });
      return;
    }

    if (!this.canSignOffActivity(activity)) {
      this.snackBar.open('You are not authorized to sign off this activity. Your role does not match the required signoff role.', 'Close', {
        duration: 4000
      });
      return;
    }

    if (activity.is_complete) {
      this.snackBar.open('This activity is already completed.', 'Close', {
        duration: 3000
      });
      return;
    }

    // Sign-off uses current user (manager/admin) from auth/localStorage; backend looks up their initials from employees3
    this.processingSignOff = true;
    activity.is_complete = true;
    this.cdr.detectChanges();

    this.trainingService.completeActivity(
      this.enrollment.id,
      activity.id,
      this.currentUserId,
      ''
    ).subscribe(
      (response) => {
        // Re-enable checkboxes after response
        this.processingSignOff = false;
        
        if (!response.Error) {
          this.snackBar.open('Activity completed successfully!', 'Close', {
            duration: 3000
          });
          
          // Update activity with response data (initials from backend/signer)
          if (response.ActivityCompletion) {
            activity.initials = response.ActivityCompletion.initials || response.Initials || '';
            activity.completed_at = response.ActivityCompletion.completed_at;
            activity.signed_off_by_employee_id = response.ActivityCompletion.signed_off_by_employee_id;
            activity.signed_off_by_name = response.ActivityCompletion.signed_off_by_name;
            activity.signer_initials = response.ActivityCompletion.signer_initials;
          } else if (response.Initials) {
            activity.initials = response.Initials;
          }
          
          // Update enrollment data if provided in response
          if (response.Enrollment) {
            this.enrollment.progress_pct = response.Enrollment.progress_pct || this.enrollment.progress_pct;
            // Update any other enrollment fields that might have changed
            if (response.Enrollment.employee_initials) {
              this.enrollment.employee_initials = response.Enrollment.employee_initials;
            }
          }
          
          // Update progress percentage
          this.updateProgressAfterActivityCompletion();
        } else {
          // Revert optimistic update on error
          activity.is_complete = false;
          this.cdr.detectChanges();
          this.snackBar.open('Error: ' + response.Message, 'Close', {
            duration: 5000
          });
        }
      },
      (error) => {
        // Re-enable checkboxes after error
        this.processingSignOff = false;
        
        // Revert optimistic update on error
        activity.is_complete = false;
        this.cdr.detectChanges();
        console.error('Error completing activity:', error);
        this.snackBar.open('Error completing activity', 'Close', {
          duration: 5000
        });
      }
    );
  }

  updateProgressAfterActivityCompletion(): void {
    if (!this.enrollment || !this.enrollment.activities) {
      return;
    }

    // Recalculate progress percentages
    const totalTracked = this.getTotalTrackedCount();
    const completed = this.getCompletedCount();
    
    // Update enrollment progress if needed
    if (this.enrollment.activities && totalTracked > 0) {
      const newProgress = Math.round((completed / totalTracked) * 100);
      // Only update if it's different to avoid unnecessary change detection
      if (this.enrollment.progress_pct !== newProgress) {
        this.enrollment.progress_pct = newProgress;
      }
    }
    
    this.cdr.detectChanges();
  }

  getTimelineLabel(activity: any): string {
    if (activity.timeline_label) {
      return activity.timeline_label;
    }
    if (activity.timeline_day !== null && activity.timeline_day !== undefined) {
      return `Day ${activity.timeline_day}`;
    }
    return '';
  }

  getStatusClass(activity: any): string {
    if (activity.is_complete) {
      return 'status-complete';
    }
    if (this.isActivityLockedForUser(activity)) {
      return 'status-locked';
    }
    return 'status-available';
  }

  formatTrainingLevel(level: number | string): string {
    if (level === null || level === undefined) {
      return '0.000';
    }
    const numLevel = typeof level === 'string' ? parseFloat(level) : level;
    if (isNaN(numLevel)) {
      return '0.000';
    }
    return numLevel.toFixed(3);
  }

  getCompletedCount(): number {
    if (!this.enrollment || !this.enrollment.activities) {
      return 0;
    }
    return this.enrollment.activities.filter((a: any) => a.is_tracked && a.is_complete).length;
  }

  getTotalTrackedCount(): number {
    if (!this.enrollment || !this.enrollment.activities) {
      return 0;
    }
    return this.enrollment.activities.filter((a: any) => a.is_tracked).length;
  }

  getActivitiesProgressPct(): number {
    const total = this.getTotalTrackedCount();
    if (total === 0) {
      return 0;
    }
    const completed = this.getCompletedCount();
    return Math.round((completed / total) * 100);
  }

  areAllActivitiesCompleted(): boolean {
    const total = this.getTotalTrackedCount();
    if (total === 0) {
      return false;
    }
    const completed = this.getCompletedCount();
    return completed === total;
  }

  getProgressBarStyle(width: number): any {
    return {
      'width': width + '%',
      'background-color': '#424242',
      'background': '#424242',
      'height': '100%',
      'transition': 'width 0.3s ease'
    };
  }

  updateInitials(activity: any, event: any): void {
    const initials = event.target.value.trim().toUpperCase();
    // Store initials temporarily (could be saved to backend if needed)
    activity.initials = initials;
  }

  toggleSignOff(activity: any, event: any): void {
    if (event.checked && !activity.is_complete) {
      // Mark as complete
      this.completeActivity(activity);
    }
  }

  completeEnrollment(): void {
    if (!this.enrollment || !this.enrollment.id) {
      this.snackBar.open('Error: Enrollment not found', 'Close', {
        duration: 5000
      });
      return;
    }

    if (!this.areAllActivitiesCompleted()) {
      this.snackBar.open('Please complete all activities before completing the course.', 'Close', {
        duration: 3000
      });
      return;
    }

    this.trainingService.completeEnrollment(this.enrollment.id).subscribe(
      (response) => {
        if (!response.Error) {
          this.snackBar.open('Course completed successfully!', 'Close', {
            duration: 3000
          });
          // Update enrollment with completion date
          if (response.Enrollment) {
            this.enrollment.date_completed = response.Enrollment.date_completed;
            this.enrollment.status = response.Enrollment.status || 'COMPLETED';
          }
          // Navigate back to dashboard after a short delay
          setTimeout(() => {
            this.goBack();
          }, 1500);
        } else {
          this.snackBar.open('Error: ' + response.Message, 'Close', {
            duration: 5000
          });
        }
      },
      (error) => {
        console.error('Error completing enrollment:', error);
        this.snackBar.open('Error completing course', 'Close', {
          duration: 5000
        });
      }
    );
  }

  goBack(): void {
    this.router.navigate(['/training']);
  }
}
